/** The Mythic hooks added for Fire, Lightning, Ballista, Poison and Beacon (DESIGN §9.4), resolved by id once per run. */
import { BalanceData } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { MYTHIC_ARCANE_OVERCHARGE, MYTHIC_BALLISTA_PIERCE, MYTHIC_BARRACKS_FOURTH, MYTHIC_BEACON_RANGE, MYTHIC_FIRE_SPREAD, MYTHIC_HEX_DEEP_CURSE, MYTHIC_LIGHTNING_CHAIN, MYTHIC_MORTAR_CLUSTER, MYTHIC_POISON_SHRED } from "../meta/mythic";
import { distanceSquared } from "../world/roadGeometry";
import { canBurn } from "./affixes";
import { Enemy } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { igniteBurn } from "./systems/enemyMotion";

/** One effect: its index in `gear.mythicEffects` (−1 when the balance has none by that id) and its number. */
export interface MythicHook { readonly effect: number; readonly value: Fix64 }

export interface MythicHooks {
  /** Wildfire: a burning enemy's death relights the burn on the nearest enemy within `value` cells. */
  readonly fireSpread: MythicHook;
  /** Forked Bolt: `value` more chain hops. */
  readonly lightningChain: MythicHook;
  /** Skewer: `value` more enemies pierced. */
  readonly ballistaPierce: MythicHook;
  /** Deep Rot: the shred cap rises by `value` stacks. */
  readonly poisonShred: MythicHook;
  /** Wider Beacon: the aura (the tower's range) reaches `value` cells further. */
  readonly beaconRange: MythicHook;
  /** Cluster Shells: Mortar shells burst `value` cells wider. */
  readonly mortarCluster: MythicHook;
  /** Fourth Shield: a Barracks posts `value` more soldiers. */
  readonly barracksFourth: MythicHook;
  /** Overcharge: the Arcane beam's full-ramp multiplier rises by `value`. */
  readonly arcaneOvercharge: MythicHook;
  /** Deep Curse: a Hex curse adds `value` to the damage taken. */
  readonly hexDeepCurse: MythicHook;
}

export function resolveMythicHooks(balance: BalanceData): MythicHooks {
  const hook = (id: string): MythicHook => {
    const m = balance.gear.mythicEffects.find((x) => x.id === id);
    return m ? { effect: m.index, value: m.value } : { effect: -1, value: Fix64.Zero };
  };
  return { fireSpread: hook(MYTHIC_FIRE_SPREAD), lightningChain: hook(MYTHIC_LIGHTNING_CHAIN), ballistaPierce: hook(MYTHIC_BALLISTA_PIERCE), poisonShred: hook(MYTHIC_POISON_SHRED), beaconRange: hook(MYTHIC_BEACON_RANGE),
    mortarCluster: hook(MYTHIC_MORTAR_CLUSTER), barracksFourth: hook(MYTHIC_BARRACKS_FOURTH), arcaneOvercharge: hook(MYTHIC_ARCANE_OVERCHARGE), hexDeepCurse: hook(MYTHIC_HEX_DEEP_CURSE) };
}

/** The hook's number when archetype `archetype` wears it this run, else zero. */
export function mythicValue(sim: LevelSim, archetype: number, hook: MythicHook): Fix64 {
  return hook.effect >= 0 && sim.gear[archetype].mythic.includes(hook.effect) ? hook.value : Fix64.Zero;
}

/**
 * Wildfire: each burn stack on the dying enemy lit by a living tower wearing the effect is relit on the nearest other
 * enemy that tower could hit and that can burn, within the effect's reach (ties → lower id), at the stack's strength
 * and the tower's full burn duration. Stacks in the order they were lit, so the result is fixed by the state.
 */
export function spreadBurnOnDeath(sim: LevelSim, dead: Enemy): void {
  const hook = sim.mythics.fireSpread;
  if (hook.effect < 0) return;
  for (let i = 0; i < dead.burnStackSource.length; i++) {
    const t = sim.findTower(dead.burnStackSource[i]);
    if (t === null) continue;
    const reach = mythicValue(sim, t.archetype, hook);
    if (!reach.isPositive()) continue;
    const a = sim.balance.towers[t.archetype];
    const r2 = reach.mul(reach);
    let best: Enemy | null = null; let bestD2 = Fix64.Zero;
    for (const e of sim.enemySlots) {
      if (!e.alive || e === dead || e.burrowed || !(e.isAir ? a.targetsAir : a.targetsGround) || !canBurn(sim, e)) continue;
      const d2 = distanceSquared(dead.x, dead.y, e.x, e.y);
      if (d2.gt(r2)) continue;
      if (best === null || d2.lt(bestD2) || (d2.eq(bestD2) && e.id < best.id)) { best = e; bestD2 = d2; }
    }
    if (best === null) continue;
    igniteBurn(best, t.id, dead.burnStackPerTick[i], a.burnDurationTicks, a.burnMaxStacks);
    sim.events.add(SimEventKind.EnemyBurning, best.id, t.id, a.burnDurationTicks);
  }
}
